'use client';

import React, { useState, useEffect } from 'react';
import { useUser } from '@/lib/context/user-context';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, X } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { SpinnerIcon } from '../ui/SpinnerIcon';

interface Episode {
  shareId: string;
  title: string;
  author: string;
  isFeatured?: boolean;
}

export function FeaturedEpisodesManager() {
  const { user } = useUser();
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [featuredEpisodes, setFeaturedEpisodes] = useState<string[]>([]);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newEpisodeId, setNewEpisodeId] = useState('');

  // Check if user is authorized (fixed email)
  const isAuthorized = user?.email === 'wenyonghe666@gmail.com';

  // Get current episode ID from URL if on episode page
  const getCurrentEpisodeId = () => {
    const match = pathname?.match(/\/episodes\/([^\/\?]+)/);
    return match ? match[1] : '';
  };

  // Get auth header from Supabase
  const getAuthHeader = async () => {
    try {
      // First try: Get from user object (if useUser hook provides session)
      if ((user as any)?.session?.access_token) {
        console.log('Got token from user session');
        return `Bearer ${(user as any).session.access_token}`;
      }
      
      // Second try: Get current session from Supabase directly
      const { data: { session }, error } = await import('@/core/supabase').then(m => m.supabase.auth.getSession());
      if (session?.access_token && !error) {
        console.log('Got token from Supabase session');
        return `Bearer ${session.access_token}`;
      }
      
      // Third try: Get from localStorage (Supabase auth storage)
      const keys = Object.keys(localStorage);
      const authKey = keys.find(key => key.includes('supabase') && key.includes('auth-token'));
      
      if (authKey) {
        const authData = JSON.parse(localStorage.getItem(authKey) || '{}');
        if (authData.access_token) {
          console.log('Got token from localStorage');
          return `Bearer ${authData.access_token}`;
        }
      }
      
      console.log('No auth token found');
    } catch (error) {
      console.error('Error getting auth header:', error);
    }
    
    return null;
  };

  // Fetch featured episodes
  const fetchFeaturedEpisodes = async () => {
    try {
      const authHeader = await getAuthHeader();
      const response = await fetch('/api/admin/featured-episodes', {
        headers: authHeader ? { 'Authorization': authHeader } : {},
      });
      if (response.ok) {
        const data = await response.json();
        setFeaturedEpisodes(data.episodeIds || []);
      } else {
        console.error('Failed to fetch featured episodes:', await response.text());
      }
    } catch (error) {
      console.error('Error fetching featured episodes:', error);
    }
  };

  // Fetch all episodes from Redis
  const fetchAllEpisodes = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/episodes');
      if (response.ok) {
        const data = await response.json();
        setEpisodes(data || []);
      }
    } catch (error) {
      console.error('Error fetching episodes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && isAuthorized) {
      fetchFeaturedEpisodes();
      fetchAllEpisodes();
      
      // Auto-fill current episode ID if on episode page and input is empty
      const currentEpisodeId = getCurrentEpisodeId();
      if (currentEpisodeId && !newEpisodeId) {
        setNewEpisodeId(currentEpisodeId);
      }
    }
  }, [isOpen, isAuthorized]);

  // Update episode ID when pathname changes and modal is open
  useEffect(() => {
    if (isOpen && isAuthorized) {
      const currentEpisodeId = getCurrentEpisodeId();
      if (currentEpisodeId && !newEpisodeId) {
        setNewEpisodeId(currentEpisodeId);
      }
    }
  }, [pathname, isOpen, isAuthorized, newEpisodeId]);

  // Add episode to featured
  const addFeaturedEpisode = async (episodeId: string) => {
    try {
      const authHeader = await getAuthHeader();
      console.log('Adding episode with auth header:', authHeader ? 'Present' : 'Missing');
      
      const response = await fetch('/api/admin/featured-episodes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authHeader ? { 'Authorization': authHeader } : {}),
        },
        body: JSON.stringify({ episodeIds: [episodeId] }),
      });

      if (response.ok) {
        setFeaturedEpisodes([...featuredEpisodes, episodeId]);
        setNewEpisodeId('');
      } else {
        const errorText = await response.text();
        console.error('Server error:', errorText);
        alert(`Error: ${errorText}`);
      }
    } catch (error) {
      console.error('Error adding featured episode:', error);
      alert('Failed to add featured episode');
    }
  };

  // Remove episode from featured
  const removeFeaturedEpisode = async (episodeId: string) => {
    if (!confirm('Are you sure you want to remove this episode from featured?')) {
      return;
    }

    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`/api/admin/featured-episodes/${episodeId}`, {
        method: 'DELETE',
        headers: authHeader ? { 'Authorization': authHeader } : {},
      });

      if (response.ok) {
        setFeaturedEpisodes(featuredEpisodes.filter(id => id !== episodeId));
      } else {
        const errorText = await response.text();
        console.error('Server error:', errorText);
        alert(`Error: ${errorText}`);
      }
    } catch (error) {
      console.error('Error removing featured episode:', error);
      alert('Failed to remove featured episode');
    }
  };

  // Don't render anything if not authorized
  if (!isAuthorized) {
    return null;
  }

  return (
    <>
      {/* Floating button to open manager */}
      {/* <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:bg-primary/90 transition-colors"
        title="Manage Featured Episodes"
      >
        <Plus className="h-5 w-5" />
      </button> */}

      {/* Manager Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-background border border-border rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-xl font-semibold">Featured Episodes Manager</h2>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-muted rounded-md transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
              {/* Add new featured episode */}
              <div className="mb-6">
                <h3 className="text-sm font-medium mb-2">Add Featured Episode</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newEpisodeId}
                    onChange={(e) => setNewEpisodeId(e.target.value)}
                    placeholder="Enter episode ID"
                    className="flex-1 px-3 py-2 bg-muted border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <Button
                    onClick={() => addFeaturedEpisode(newEpisodeId)}
                    disabled={!newEpisodeId.trim()}
                  >
                    Add
                  </Button>
                </div>
              </div>

              {/* Current featured episodes */}
              <div className="mb-6">
                <h3 className="text-sm font-medium mb-2">
                  Current Featured Episodes ({featuredEpisodes.length})
                </h3>
                {featuredEpisodes.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No featured episodes</p>
                ) : (
                  <div className="space-y-2">
                    {featuredEpisodes.map((episodeId) => {
                      const episode = episodes.find(e => e.shareId === episodeId);
                      return (
                        <div
                          key={episodeId}
                          className="flex items-center justify-between p-3 bg-muted/50 rounded-md"
                        >
                          <div>
                            <p className="font-medium">{episode?.title || episodeId}</p>
                            {episode && (
                              <p className="text-sm text-muted-foreground">
                                by {episode.author}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => removeFeaturedEpisode(episodeId)}
                            className="p-1 hover:bg-background rounded-md transition-colors text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* All episodes list */}
              <div>
                <h3 className="text-sm font-medium mb-2">All Episodes</h3>
                {isLoading ? (
                  // <div className="flex justify-center py-8">
                  //   <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  // </div>
                  <SpinnerIcon size={32} color="hsl(var(--primary))" />
                ) : episodes.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No episodes found</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {episodes.map((episode) => {
                      const isFeatured = featuredEpisodes.includes(episode.shareId);
                      return (
                        <div
                          key={episode.shareId}
                          className={`flex items-center justify-between p-3 rounded-md ${
                            isFeatured ? 'bg-primary/10 border border-primary/20' : 'bg-muted/30'
                          }`}
                        >
                          <div className="flex-1">
                            <p className="font-medium">{episode.title}</p>
                            <p className="text-sm text-muted-foreground">
                              {episode.author} • ID: {episode.shareId}
                            </p>
                          </div>
                          <div className="ml-4">
                            {isFeatured ? (
                              <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">
                                Featured
                              </span>
                            ) : (
                              <button
                                onClick={() => addFeaturedEpisode(episode.shareId)}
                                className="text-xs bg-muted hover:bg-muted/80 px-2 py-1 rounded transition-colors"
                              >
                                Add to Featured
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
